import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Alert, Spinner, ListPage, PageHeader, SegmentedControl } from '@/components/ui';
import { useSearchParams } from 'react-router-dom';
import { workIdService } from '../../../services/workIdService';
import { taskService as quantifiedTaskService } from '../../../services/quantifiedTaskService';
import type { WorkIdDetail } from '../../../services/workIdService';
import { workIdService as workIdFeatureService } from '../workIdService';
import type { WorkIdRecord, WorkIdFilterOptions, WorkIdListFilters } from '../workIdService';
import { useAuth } from '../../../contexts/AuthContext';
import { usePermissions } from '../../../hooks/usePermissions';
import { usePersistedListState } from '../../../hooks/usePersistedListState';
import WorkIdsTable from '../components/WorkIdsTable';
import WorkIdsMapView from '../components/WorkIdsMapView';
import WorkIdFilters from '../components/WorkIdFilters';
import WorkIdApproveModal from '../components/WorkIdApproveModal';
import WorkIdCancelModal from '../components/WorkIdCancelModal';
import WorkIdCloseWithoutActionModal from '../components/WorkIdCloseWithoutActionModal';
import WorkIdReopenModal from '../components/WorkIdReopenModal';
import WorkIdHistoryModal from '../components/WorkIdHistoryModal';
import WorkIdTransitionToTaskModal from '../components/WorkIdTransitionToTaskModal';
import WorkIdMergeModal from '../components/WorkIdMergeModal';
import WorkIdMergeSuggestionsModal from '../components/WorkIdMergeSuggestionsModal';
import WorkIdTaskReopenModal from '../components/WorkIdTaskReopenModal';
import WorkIdDetailModal from '../components/WorkIdDetailModal';
import WorkIdUnmergeModal from '../components/WorkIdUnmergeModal';
import WorkIdUnassignModal from '../components/WorkIdUnassignModal';
import WorkIdCreateModal from '../components/WorkIdCreateModal';
import WorkIdCloseTaskModal from '../components/WorkIdCloseTaskModal';
import CompletionModal from '../../quantified-tracking/components/taskModals/CompletionModal';

type ViewMode = 'list' | 'map';

const DEFAULT_FILTERS: WorkIdListFilters = {
  serviceAreaId: '',
  foremanAreaId: '',
  assetType: '',
  status: 'exclude-cancelled',
  search: '',
  priority: '',
  dateFrom: '',
  dateTo: '',
  dateFilterType: 'reportedOn',
  reportedById: '',
  workSource: ''
};

/** Query parameters a link can preset the filters with. */
const URL_FILTER_KEYS = ['serviceAreaId', 'foremanAreaId', 'assetType', 'status', 'priority', 'reportedById', 'workSource'];

/** sessionStorage page key for the remembered list state (V2-024, FLT-09). */
const PAGE_KEY = 'workIds';

const WorkIdsPage: React.FC = () => {
  const { loading, currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canRegenerateSuggestions =
    hasPermission('workId:regenerate-merge-suggestions') ||
    (currentUser?.role?.name?.toLowerCase() === 'admin');
  const canCreateWorkId = hasPermission('workId:create');
  const [searchParams] = useSearchParams();
  // A link that presets filters wins over the remembered ones; without one, the page comes back
  // as the user left it (V2-024).
  const urlHasFilters = URL_FILTER_KEYS.some((key) => searchParams.has(key));
  const initialFiltersFromUrl = useMemo<WorkIdListFilters>(
    () => ({
      ...DEFAULT_FILTERS,
      serviceAreaId: searchParams.get('serviceAreaId') ?? '',
      foremanAreaId: searchParams.get('foremanAreaId') ?? '',
      assetType: searchParams.get('assetType') ?? '',
      status: searchParams.get('status') ?? 'exclude-cancelled',
      priority: searchParams.get('priority') ?? '',
      reportedById: searchParams.get('reportedById') ?? '',
      workSource: searchParams.get('workSource') ?? ''
    }),
    [searchParams]
  );
  const [viewMode, setViewMode] = usePersistedListState<ViewMode>(PAGE_KEY, 'view', 'list');
  const [filters, setFilters] = usePersistedListState<WorkIdListFilters>(
    PAGE_KEY,
    'filters',
    urlHasFilters ? initialFiltersFromUrl : DEFAULT_FILTERS
  );
  const [filterOptions, setFilterOptions] = useState<WorkIdFilterOptions>({
    serviceAreas: [],
    foremanAreas: [],
    reporters: [],
    sources: []
  });
  const [regenerating, setRegenerating] = useState(false);
  const [regenerateMessage, setRegenerateMessage] = useState<{ type: 'success' | 'danger'; text: string } | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [approveModalDetail, setApproveModalDetail] = useState<WorkIdDetail | null>(null);
  const [cancelModalDetail, setCancelModalDetail] = useState<WorkIdDetail | null>(null);
  const [closeWithoutActionModalDetail, setCloseWithoutActionModalDetail] = useState<WorkIdDetail | null>(null);
  const [reopenModalDetail, setReopenModalDetail] = useState<WorkIdDetail | null>(null);
  const [historyModalDetail, setHistoryModalDetail] = useState<WorkIdDetail | null>(null);
  const [assignModalRecord, setAssignModalRecord] = useState<WorkIdRecord | null>(null);
  const [assignModalInitialStep, setAssignModalInitialStep] = useState<'assign-existing' | 'create-select-type'>('assign-existing');
  const [mergeModalRecord, setMergeModalRecord] = useState<WorkIdRecord | null>(null);
  const [mergeSuggestionsModalRecord, setMergeSuggestionsModalRecord] = useState<WorkIdRecord | null>(null);
  const [taskReopenDetail, setTaskReopenDetail] = useState<WorkIdDetail | null>(null);
  const [detailModalWorkId, setDetailModalWorkId] = useState<WorkIdDetail | null>(null);
  const [detailStartInEditMode, setDetailStartInEditMode] = useState(false);
  const [unmergeModalDetail, setUnmergeModalDetail] = useState<WorkIdDetail | null>(null);
  const [unassignModalDetail, setUnassignModalDetail] = useState<WorkIdDetail | null>(null);
  const [closeTaskDetail, setCloseTaskDetail] = useState<WorkIdDetail | null>(null);
  const [reportAccomplishmentTask, setReportAccomplishmentTask] = useState<import('../../../types/Task').Task | null>(null);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [appliedSearch, setAppliedSearch] = usePersistedListState(PAGE_KEY, 'search', '');
  const [searchInput, setSearchInput] = useState(appliedSearch);
  const [clearFiltersTrigger, setClearFiltersTrigger] = useState(0);

  useEffect(() => {
    const searchFromUrl = searchParams.get('search');
    if (searchFromUrl != null && searchFromUrl.trim() !== '') {
      setSearchInput(searchFromUrl.trim());
      setAppliedSearch(searchFromUrl.trim());
    }
  }, [searchParams, setAppliedSearch]);

  const handleModalSuccess = useCallback(() => {
    setRefreshKey((prev) => prev + 1);
    setFetchError(null);
  }, []);

  const handleSearch = (value: string) => {
    setAppliedSearch(value.trim());
  };

  const handleClearAllFilters = useCallback(() => {
    setSearchInput('');
    setAppliedSearch('');
    setFilters(DEFAULT_FILTERS);
    setClearFiltersTrigger((prev) => prev + 1);
  }, [setAppliedSearch, setFilters]);

  useEffect(() => {
    if (urlHasFilters) setFilters(initialFiltersFromUrl);
    // Only when the link's filters change: otherwise the remembered filters stand.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialFiltersFromUrl]);

  useEffect(() => {
    workIdFeatureService
      .getFilterOptions()
      .then((options) =>
        setFilterOptions({
          ...options,
          reporters: options.reporters ?? [],
          sources: options.sources ?? []
        })
      )
      .catch(() => {});
  }, []);

  const fetchAndOpen = useCallback(
    async (
      record: WorkIdRecord,
      setter: (detail: WorkIdDetail | null) => void
    ) => {
      setFetchError(null);
      setDetailLoading(true);
      try {
        const detail = await workIdService.getById(record.id);
        setter(detail);
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : 'Failed to load work ID details.');
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  const handleApprove = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setApproveModalDetail);
    },
    [fetchAndOpen]
  );

  const handleCancel = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setCancelModalDetail);
    },
    [fetchAndOpen]
  );

  const handleCloseWithoutAction = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setCloseWithoutActionModalDetail);
    },
    [fetchAndOpen]
  );

  const handleReopen = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setReopenModalDetail);
    },
    [fetchAndOpen]
  );

  const handleReviewHistory = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setHistoryModalDetail);
    },
    [fetchAndOpen]
  );

  const handleAssignExisting = useCallback((record: WorkIdRecord) => {
    setAssignModalRecord(record);
    setAssignModalInitialStep('assign-existing');
    setFetchError(null);
  }, []);

  const handleCreateNewTask = useCallback((record: WorkIdRecord) => {
    setAssignModalRecord(record);
    setAssignModalInitialStep('create-select-type');
    setFetchError(null);
  }, []);

  const handleMerge = useCallback((record: WorkIdRecord) => {
    setMergeModalRecord(record);
    setFetchError(null);
  }, []);

  const handleViewMergeSuggestions = useCallback((record: WorkIdRecord) => {
    setMergeSuggestionsModalRecord(record);
    setFetchError(null);
  }, []);

  /**
   * Open detail modal in view-only mode (e.g., row click).
   */
  const handleOpenDetail = useCallback(
    (record: WorkIdRecord) => {
      setDetailStartInEditMode(false);
      fetchAndOpen(record, setDetailModalWorkId);
    },
    [fetchAndOpen]
  );

  /**
   * Open detail modal directly in edit mode (action-bar Edit).
   */
  const handleEdit = useCallback(
    (record: WorkIdRecord) => {
      setDetailStartInEditMode(true);
      fetchAndOpen(record, setDetailModalWorkId);
    },
    [fetchAndOpen]
  );

  const handleOpenMergedParent = useCallback(
    (record: WorkIdRecord) => {
      const parentId = record.mergedIntoParent?.id;
      if (parentId == null) {
        setFetchError('Parent work ID not found for this merged record.');
        return;
      }
      setDetailStartInEditMode(false);
      fetchAndOpen({ id: parentId } as WorkIdRecord, setDetailModalWorkId);
    },
    [fetchAndOpen]
  );

  const handleReopenTask = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setTaskReopenDetail);
    },
    [fetchAndOpen]
  );

  const handleUnmerge = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setUnmergeModalDetail);
    },
    [fetchAndOpen]
  );

  const handleUnassign = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setUnassignModalDetail);
    },
    [fetchAndOpen]
  );

  const handleCloseTask = useCallback(
    (record: WorkIdRecord) => {
      fetchAndOpen(record, setCloseTaskDetail);
    },
    [fetchAndOpen]
  );

  const handleReportAccomplishment = useCallback(
    async (record: WorkIdRecord) => {
      const task = record.task;
      if (!task || task.type !== 'Quantified') return;
      setFetchError(null);
      setDetailLoading(true);
      try {
        const fullTask = await quantifiedTaskService.getTask(task.id);
        setReportAccomplishmentTask(fullTask);
      } catch (err) {
        setFetchError(err instanceof Error ? err.message : 'Failed to load task details.');
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  const closeApproveModal = () => {
    setApproveModalDetail(null);
    setFetchError(null);
  };

  const closeCancelModal = () => {
    setCancelModalDetail(null);
    setFetchError(null);
  };

  const closeCloseWithoutActionModal = () => {
    setCloseWithoutActionModalDetail(null);
    setFetchError(null);
  };

  const closeReopenModal = () => {
    setReopenModalDetail(null);
    setFetchError(null);
  };

  const closeHistoryModal = () => {
    setHistoryModalDetail(null);
    setFetchError(null);
  };

  const closeAssignModal = () => {
    setAssignModalRecord(null);
    setFetchError(null);
  };

  const closeMergeModal = () => {
    setMergeModalRecord(null);
    setFetchError(null);
  };

  const closeMergeSuggestionsModal = () => {
    setMergeSuggestionsModalRecord(null);
    setFetchError(null);
  };

  const closeTaskReopenModal = () => {
    setTaskReopenDetail(null);
    setFetchError(null);
  };

  const closeDetailModal = () => {
    setDetailModalWorkId(null);
    setFetchError(null);
    setDetailStartInEditMode(false);
  };

  const closeUnmergeModal = () => {
    setUnmergeModalDetail(null);
    setFetchError(null);
  };

  const closeUnassignModal = () => {
    setUnassignModalDetail(null);
    setFetchError(null);
  };

  const closeCloseTaskModal = () => {
    setCloseTaskDetail(null);
    setFetchError(null);
  };

  const closeReportAccomplishmentModal = () => {
    setReportAccomplishmentTask(null);
    setFetchError(null);
  };

  const handleRegenerateMergeSuggestions = useCallback(async () => {
    setRegenerateMessage(null);
    setRegenerating(true);
    try {
      const result = await workIdService.regenerateAllMergeSuggestions();
      setRegenerateMessage({
        type: 'success',
        text: `Regenerated merge suggestions for ${result.processed} of ${result.total} Work IDs.${result.errors?.length ? ` ${result.errors.length} error(s).` : ''}`,
      });
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      setRegenerateMessage({
        type: 'danger',
        text: err instanceof Error ? err.message : 'Failed to regenerate merge suggestions.',
      });
    } finally {
      setRegenerating(false);
    }
  }, []);

  if (loading) {
    return <div className="tw-w-full tw-p-4">Loading...</div>;
  }

  // Header, filters, search row and notices - the same in both views.
  const pageTop = (
    <>
      {/* The shared page header (W-M9, W-iP8): fixed order with Create Work ID last, labels at
          every width, and the List/Map toggle in the view-toggle slot under the title. The
          header's own Clear All is gone: the filter panel's Clear All also clears the search. */}
      <PageHeader
        title="Work IDs"
        actions={[
          {
            key: 'refresh',
            label: 'Refresh',
            // Was `variant="success"` (green) — Refresh is chrome, not a success outcome.
            icon: 'bi-arrow-clockwise',
            intent: 'neutral',
            overflow: true,
            onClick: () => setRefreshKey((prev) => prev + 1),
          },
          {
            key: 'regenerate',
            label: regenerating ? 'Regenerating…' : 'Regenerate Merge Suggestions',
            icon: regenerating ? 'bi-hourglass-split' : 'bi-diagram-3',
            intent: 'secondary',
            overflow: true,
            hidden: !canRegenerateSuggestions,
            disabled: regenerating,
            title: 'Regenerate merge suggestions for all eligible Work IDs',
            onClick: handleRegenerateMergeSuggestions,
          },
          {
            key: 'create',
            label: 'Create Work ID',
            icon: 'bi-plus-lg',
            intent: 'primary',
            hidden: !canCreateWorkId,
            title: 'Create a new Work ID',
            onClick: () => setCreateModalVisible(true),
          },
        ]}
        viewToggle={
          <SegmentedControl
            aria-label="Work ID display"
            value={viewMode}
            onChange={setViewMode}
            options={[
              { value: 'list', label: 'List', icon: 'bi-list-ul' },
              { value: 'map', label: 'Map', icon: 'bi-map' },
            ]}
          />
        }
      />

      <div className="tw-shrink-0">
        <WorkIdFilters
          filterOptions={filterOptions}
          filters={filters}
          searchActive={!!appliedSearch}
          // V2-077: no header Search button; the search is always visible in the panel header.
          search={{
            value: searchInput,
            onChange: setSearchInput,
            onSearch: handleSearch,
            placeholder: 'Work ID, comments, asset or reporter',
            label: 'Search Work IDs',
          }}
          onClearFilters={handleClearAllFilters}
          onApplyFilters={(newFilters) =>
            setFilters((prev) => ({ ...prev, ...newFilters, search: prev.search ?? '' }))
          }
        />
      </div>

      {fetchError && (
        <Alert variant="danger" dismissible onClose={() => setFetchError(null)} className="tw-shrink-0">
          {fetchError}
        </Alert>
      )}

      {regenerateMessage && (
        <Alert variant={regenerateMessage.type} dismissible onClose={() => setRegenerateMessage(null)} className="tw-shrink-0">
          {regenerateMessage.text}
        </Alert>
      )}

      {detailLoading && (
        <div className="tw-flex tw-items-center tw-gap-2 tw-mb-2 tw-text-content-muted tw-text-sm">
          <Spinner as="span" size="sm" aria-hidden="true" />
          Loading work ID details…
        </div>
      )}
    </>
  );

  // The list view is on the shared list layout (W-H5): the table box fills the height under the
  // filters and is the ONE scroll area. The map view keeps its original page container: the map
  // sizes itself against a fixed-height, non-scrolling column.
  // A plain function (not a component defined in render), so the root element type only changes
  // when the view does and re-renders never remount the page.
  const isList = viewMode === 'list';
  const wrapPageRoot = (content: React.ReactNode) =>
    isList ? (
      <ListPage className="work-ids-page">{content}</ListPage>
    ) : (
      // V2-028: the map page scrolls as one page; it used to clip the bottom 145 px of the map,
      // zoom controls included, with nothing to scroll.
      <div className="work-ids-page wrap-list-page tw-w-full tw-p-4 tw-flex tw-flex-col tw-h-full tw-overflow-y-auto tw-overflow-x-hidden [&>*]:tw-shrink-0">{content}</div>
    );

  return wrapPageRoot(
    <>
      {pageTop}

      {isList ? (
        <ListPage.Body>
        <WorkIdsTable
          key={refreshKey}
          refreshKey={refreshKey}
          filters={filters}
          onFiltersChange={(newFilters) =>
            setFilters((prev) => ({
              ...prev,
              ...newFilters,
              search: newFilters.search ?? prev.search ?? ''
            }))
          }
          filterOptions={filterOptions}
          search={appliedSearch}
          onSearchCleared={() => {
            setSearchInput('');
            setAppliedSearch('');
          }}
          clearFiltersTrigger={clearFiltersTrigger}
          hideFilters
          onApprove={handleApprove}
          onCancel={handleCancel}
          onCloseWithoutAction={handleCloseWithoutAction}
          onReopen={handleReopen}
          onAssignExisting={handleAssignExisting}
          onCreateNewTask={handleCreateNewTask}
          onUnassign={handleUnassign}
          onMerge={handleMerge}
          onViewMergeSuggestions={handleViewMergeSuggestions}
          onUnmerge={handleUnmerge}
          onReviewHistory={handleReviewHistory}
          onOpenDetail={handleOpenDetail}
          onEdit={handleEdit}
          onReopenTask={handleReopenTask}
          onReportAccomplishment={handleReportAccomplishment}
          onCloseTask={handleCloseTask}
          onOpenMergedParent={handleOpenMergedParent}
        />
        </ListPage.Body>
      ) : (
        <div className="tw-grow tw-flex tw-flex-col tw-min-h-0" style={{ minHeight: 0 }}>
          <WorkIdsMapView
            key={refreshKey}
            refreshKey={refreshKey}
            filters={filters}
            search={appliedSearch}
            filterOptions={filterOptions}
            onApprove={handleApprove}
            onCancel={handleCancel}
            onCloseWithoutAction={handleCloseWithoutAction}
            onReopen={handleReopen}
            onAssignExisting={handleAssignExisting}
            onCreateNewTask={handleCreateNewTask}
            onUnassign={handleUnassign}
            onMerge={handleMerge}
            onViewMergeSuggestions={handleViewMergeSuggestions}
            onUnmerge={handleUnmerge}
            onReviewHistory={handleReviewHistory}
            onEdit={handleEdit}
            onReopenTask={handleReopenTask}
            onReportAccomplishment={handleReportAccomplishment}
            onCloseTask={handleCloseTask}
          />
        </div>
      )}

      <WorkIdApproveModal
        visible={!!approveModalDetail}
        onClose={closeApproveModal}
        workId={approveModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeApproveModal();
        }}
      />

      <WorkIdCancelModal
        visible={!!cancelModalDetail}
        onClose={closeCancelModal}
        workId={cancelModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeCancelModal();
        }}
      />

      <WorkIdCloseWithoutActionModal
        visible={!!closeWithoutActionModalDetail}
        onClose={closeCloseWithoutActionModal}
        workId={closeWithoutActionModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeCloseWithoutActionModal();
        }}
      />

      <WorkIdReopenModal
        visible={!!reopenModalDetail}
        onClose={closeReopenModal}
        workId={reopenModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeReopenModal();
        }}
      />

      <WorkIdHistoryModal
        visible={!!historyModalDetail}
        onClose={closeHistoryModal}
        workId={historyModalDetail}
      />

      <WorkIdTransitionToTaskModal
        visible={!!assignModalRecord}
        onClose={closeAssignModal}
        workId={assignModalRecord}
        initialStep={assignModalInitialStep}
        onSuccess={() => {
          handleModalSuccess();
          closeAssignModal();
        }}
      />

      <WorkIdMergeModal
        visible={!!mergeModalRecord}
        onClose={closeMergeModal}
        workId={mergeModalRecord}
        onSuccess={() => {
          handleModalSuccess();
          closeMergeModal();
        }}
      />

      <WorkIdMergeSuggestionsModal
        visible={!!mergeSuggestionsModalRecord}
        onClose={closeMergeSuggestionsModal}
        workId={mergeSuggestionsModalRecord}
        onSuccess={() => {
          handleModalSuccess();
          closeMergeSuggestionsModal();
        }}
      />

      <WorkIdTaskReopenModal
        visible={!!taskReopenDetail}
        onClose={closeTaskReopenModal}
        workId={taskReopenDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeTaskReopenModal();
        }}
      />

      <WorkIdDetailModal
        visible={!!detailModalWorkId}
        onClose={closeDetailModal}
        workId={detailModalWorkId}
        startInEditMode={detailStartInEditMode}
        onSuccess={() => {
          handleModalSuccess();
        }}
      />

      <WorkIdUnmergeModal
        visible={!!unmergeModalDetail}
        onClose={closeUnmergeModal}
        workId={unmergeModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeUnmergeModal();
        }}
      />

      <WorkIdUnassignModal
        visible={!!unassignModalDetail}
        onClose={closeUnassignModal}
        workId={unassignModalDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeUnassignModal();
        }}
      />

      <WorkIdCreateModal
        visible={createModalVisible}
        onClose={() => setCreateModalVisible(false)}
        onSuccess={() => {
          handleModalSuccess();
          setCreateModalVisible(false);
        }}
      />

      <WorkIdCloseTaskModal
        visible={!!closeTaskDetail}
        onClose={closeCloseTaskModal}
        workId={closeTaskDetail}
        onSuccess={() => {
          handleModalSuccess();
          closeCloseTaskModal();
        }}
      />

      {reportAccomplishmentTask && (
        <CompletionModal
          visible={!!reportAccomplishmentTask}
          onClose={closeReportAccomplishmentModal}
          task={reportAccomplishmentTask}
          onComplete={() => {
            handleModalSuccess();
            closeReportAccomplishmentModal();
          }}
        />
      )}
    </>
  );
};

export default WorkIdsPage;
